<script setup>
import { inject, ref, watch } from 'vue'
import { apolloClient } from 'boot/apollo'
import { useMeta } from 'quasar'
import { useGlobalStore } from 'src/composables/useGlobalStore'
import blogsQuery from 'src/graphql/blogsQuery.gql'
import blogCategoriesQuery from 'src/graphql/blogCategoriesQuery.gql'
import GeneralCard from 'components/general/GeneralCard.vue'
import debounce from 'debounce'
import Popper from 'vue3-popper'
import B2CContactPopup from 'components/home/B2CContactPopup.vue'
import gsap from 'gsap'

const { globalConfiguration } = useGlobalStore()
const { isBrowser, screen } = inject('Shared')

const blogs = ref([])
const blogCategories = ref()

const blogCategory = ref(null)
const page = ref(1)
const rowsPerPage = ref(12)

let loadBlogs = async (resetItems, $state) => {
  try {
    if (!resetItems) {
      page.value += 1
    } else {
      page.value = 1
    }
    const blogsData = await apolloClient.query({
      query: blogsQuery,
      variables: {
        blogsInput: {
          rowsPerPage: rowsPerPage.value,
          page: page.value,
          blogCategoryId: blogCategory.value?._id
        }
      }
    })
    const items = JSON.parse(JSON.stringify(blogsData?.data?.blogs))
    if (resetItems) {
      blogs.value = items
    } else {
      blogs.value = [...blogs.value, ...items]
      if (items.length < rowsPerPage.value) {
        $state.complete()
      } else {
        $state.loaded()
      }
    }
  } catch (e) {
    console.log('ERROR', e)
    console.log(JSON.parse(JSON.stringify(e)))
  }
}
loadBlogs(true)
loadBlogs = debounce(loadBlogs, 100)

async function loadBlogCategories () {
  try {
    const blogCategoriesData = await apolloClient.query({ query: blogCategoriesQuery })
    blogCategories.value = JSON.parse(JSON.stringify(blogCategoriesData?.data?.blogCategories))
  } catch (e) {
    console.log(JSON.parse(JSON.stringify(e)))
  }
}
await loadBlogCategories()

watch(blogCategory, async () => {
  page.value = 1
  await loadBlogs(true)
}, { deep: true })
if (globalConfiguration.value && globalConfiguration.value.pages && globalConfiguration.value.pages.blogs) {
  useMeta({
    title: globalConfiguration.value.pages.blogs.title,
    meta: {
      description: { name: 'description', content: globalConfiguration.value.pages.blogs.description },
      keywords: { name: 'keywords', content: globalConfiguration.value.pages.blogs.keyword },
      canonical: { rel: 'canonical', href: 'https://raumpioniere.ch/insights' },
      'og:title': { property: 'og:title', content: globalConfiguration.value.pages.blogs.ogTitle ? globalConfiguration.value.pages.blogs.ogTitle : globalConfiguration.value.pages.blogs.title },
      'og:image': { property: 'og:image', content: globalConfiguration.value.pages.blogs.ogImage ? globalConfiguration.value.pages.blogs.ogImage : 'https://raumpioniere.ch/icons/favicon-128x128.png' },
      'og:url': { property: 'og:url', content: 'https://raumpioniere.ch/insights' },
      'og:description': { property: 'og:description', content: globalConfiguration.value.pages.blogs.ogDescription ? globalConfiguration.value.pages.blogs.ogDescription : globalConfiguration.value.pages.blogs.description },
      'og:type': { property: 'og:type', content: globalConfiguration.value.pages.blogs.ogType ? globalConfiguration.value.pages.blogs.ogType : 'Blogs' },
      'og:locale': { property: 'og:locale', content: 'de_CH' }
    }
  })
}

const loadData = async ($state) => {
  try {
    await loadBlogs(false, $state)
  } catch (error) {
    $state.error()
  }
}

const contactVisible = ref(false)
const showMobileCategories = ref(false)
const showAllOptionMobile = ref(false)
watch(showMobileCategories, v => {
  if (v) {
    showAllOptionMobile.value = true
    gsap.to('.blogs--mobile-categories', { duration: 1, maxHeight: '400px' })
  } else {
    gsap.to('.blogs--mobile-categories', { duration: 0.5, maxHeight: '0px', onComplete: () => { showAllOptionMobile.value = false } })
  }
})

function mainBtnClicked () {
  if (screen.gt.sm) {
    blogCategory.value = null
  } else {
    if (showMobileCategories.value) {
      showMobileCategories.value = false
    }
  }
}
const hoveredCategory = ref()
</script>

<template lang="pug">
.blogs
  b2-c-contact-popup(v-model:visible="contactVisible" :always="true")
  .routerPageSize
    .blogs--header
      .blogs--header-svg
        svg(viewBox='0 0 160 202' fill='none' xmlns='http://www.w3.org/2000/svg')
          path(d='M1.52942 71.0952V199.488H88.8828V80.6152H11.0605C5.79623 80.6152 1.5405 76.3484 1.5405 71.0952C1.5405 65.8309 5.80732 61.5752 11.0605 61.5752H74.5862' stroke='#22223C' stroke-width='2' stroke-linejoin='round')
          path(d='M81.7344 71.0952H11.4595' stroke='#22223C' stroke-width='2' stroke-linejoin='round')
          path(d='M40.0083 121.377H17.0118V148.164H40.0083V121.377Z' fill='white' stroke='#22223C' stroke-width='2' stroke-linejoin='round')
          path(d='M15.9369 94.5903H77.2571' stroke='#22223C' stroke-width='2' stroke-linejoin='round')
          path(d='M15.9369 103.512H77.2571' stroke='#22223C' stroke-width='2' stroke-linejoin='round')
          path(d='M15.9369 112.444H77.2571' stroke='#22223C' stroke-width='2' stroke-linejoin='round')
          path(d='M48.9298 121.377H77.257' stroke='#22223C' stroke-width='2' stroke-linejoin='round')
          path(d='M48.9298 130.299H77.257' stroke='#22223C' stroke-width='2' stroke-linejoin='round')
          path(d='M48.9298 139.231H77.257' stroke='#22223C' stroke-width='2' stroke-linejoin='round')
          path(d='M48.9298 148.164H77.257' stroke='#22223C' stroke-width='2' stroke-linejoin='round')
          path(d='M15.9369 157.085H77.2571' stroke='#22223C' stroke-width='2' stroke-linejoin='round')
          path(d='M15.9369 166.018H77.2571' stroke='#22223C' stroke-width='2' stroke-linejoin='round')
          path(d='M15.9369 174.951H77.2571' stroke='#22223C' stroke-width='2' stroke-linejoin='round')
          path(d='M15.9369 183.872H77.2571' stroke='#22223C' stroke-width='2' stroke-linejoin='round')
          path(d='M116.999 107.734L156.974 157.063C159.28 159.911 158.847 164.09 155.999 166.395L150.702 170.695C147.853 173 143.675 172.568 141.37 169.72L101.395 120.391' fill='#1F47CD')
          path(d='M116.999 107.734L156.974 157.063C159.28 159.911 158.847 164.09 155.999 166.395L150.702 170.695C147.853 173 143.675 172.568 141.37 169.72L101.395 120.391' stroke='#22223C' stroke-width='2' stroke-linejoin='round')
          path(opacity='0.75' d='M86.0279 111.097C104.139 107.887 116.216 90.5939 113.005 72.4711C109.793 54.3483 92.5075 42.2589 74.3968 45.4686C56.2861 48.6783 44.2082 65.9717 47.42 84.0945C50.6319 102.217 67.9172 114.307 86.0279 111.097Z' fill='#5FEEBF')
          path(d='M116.8 48.6193C100.42 28.4046 70.7515 25.3014 50.5478 41.6927C30.3442 58.0839 27.2521 87.7522 43.6323 107.967C60.0124 128.182 89.6806 131.285 109.884 114.894C130.088 98.5023 133.18 68.8341 116.8 48.6193ZM101.195 104.177C86.91 115.769 65.9416 113.575 54.3492 99.2781C42.7678 84.9815 44.9511 64.002 59.2366 52.4096C73.5221 40.8171 94.4905 43.0115 106.083 57.3081C117.664 71.6047 115.481 92.5842 101.195 104.177Z' fill='white' stroke='#22223C' stroke-width='2' stroke-linejoin='round')
          path(d='M136.937 78.2876H158.459' stroke='#22223C' stroke-width='2' stroke-linejoin='round')
          path(d='M120.313 38.1577L135.529 22.9302' stroke='#22223C' stroke-width='2' stroke-linejoin='round')
          path(d='M80.1719 21.5336V0' stroke='#22223C' stroke-width='2' stroke-linejoin='round')
          path(d='M40.0416 38.1577L24.8141 22.9302' stroke='#22223C' stroke-width='2' stroke-linejoin='round')
      .blogs--header-details
        .blogs--header-title Blog
        .blogs--header-subtitle Sie interessieren sich fürs zukunftsträchtige Thema Bauen ohne Land? Dann bleiben Sie am besten mit unserem Blog am Ball!
    .blogs--categories
      .blogs--category(:class="((screen.gt.xs && blogCategory === null) || (screen.lt.sm && !showMobileCategories) || (hoveredCategory === 'ALL')) ? 'blogs--category-selected' : ''" @click="mainBtnClicked()" @mouseover="hoveredCategory = 'ALL'" @mousedown="hoveredCategory = null")
        .gt-sm Alle Beiträge
        .lt-md {{ blogCategory === null ? 'Alle Beiträge' : blogCategory.name }}
      .blogs--category.gt-xs(:class="(blogCategory?._id === item._id || (hoveredCategory === item._id)) ? 'blogs--category-selected' : ''" @mouseover="hoveredCategory = item._id" @mousedown="hoveredCategory = null" v-for="item in blogCategories" :key="item._id" @click="blogCategory = item") {{ item.name }}
      .blogs--filter-btn.lt-sm(@click="showMobileCategories = !showMobileCategories" :class="showMobileCategories ? 'blogs--filter-btn-selected' : ''")
        svg(width='14' height='9' viewBox='0 0 14 9' fill='none' xmlns='http://www.w3.org/2000/svg')
          path(d='M7.71045 8.17597L13.3605 2.51597C13.4542 2.42301 13.5286 2.3124 13.5793 2.19055C13.6301 2.06869 13.6562 1.93798 13.6562 1.80597C13.6562 1.67396 13.6301 1.54325 13.5793 1.42139C13.5286 1.29953 13.4542 1.18893 13.3605 1.09597C13.1731 0.909717 12.9196 0.805176 12.6555 0.805176C12.3913 0.805176 12.1378 0.909717 11.9505 1.09597L6.95045 6.04597L2.00045 1.09597C1.81309 0.909717 1.55964 0.805175 1.29545 0.805175C1.03127 0.805175 0.777815 0.909717 0.590452 1.09597C0.495964 1.18858 0.420794 1.29903 0.369297 1.4209C0.3178 1.54278 0.291002 1.67366 0.290453 1.80597C0.291002 1.93827 0.3178 2.06916 0.369297 2.19103C0.420793 2.31291 0.495964 2.42335 0.590452 2.51597L6.24045 8.17597C6.33409 8.27747 6.44774 8.35848 6.57424 8.41389C6.70074 8.4693 6.83735 8.4979 6.97545 8.4979C7.11355 8.4979 7.25016 8.4693 7.37666 8.41389C7.50316 8.35848 7.61681 8.27747 7.71045 8.17597Z')
    .blogs--mobile-categories.lt-sm
      .blogs--category(:class="(blogCategory === null && !showMobileCategories) ? 'blogs--category-selected' : ''" @click="blogCategory = null; showMobileCategories = false" v-if="blogCategory && showAllOptionMobile") Alle Beiträge
      .blogs--category(:class="blogCategory?._id === item._id ? 'blogs--category-selected' : ''" v-for="item in blogCategories" :key="item._id" @click="showMobileCategories = false; blogCategory = item") {{ item.name }}
    .blogs--body--items
      general-card.blogs--body--item(
        v-for="blog in blogs"
        :key="blog._id"
        :url="`/blogs/${blog.slug}`"
        :img="blog.teaserImg"
        :title="blog.teaserHeadline"
        :subtitle="blog.teaserContent"
        :categories="blog.categories"
      )
    infinite-loading(@infinite="loadData" v-if="isBrowser").infiniteLoading
      template(#complete)
        span
</template>

<style lang="sass">
.blogs
  @media screen and (min-width: 1024px)
    margin-top: 90px
  @media screen and (max-width: 1024px)
    padding: 0 10px
  .routerPageSize
    @media screen and (max-width: 1024px)
      padding-top: 0px !important
  .blogs--header
    @media screen and (min-width: 1024px)
      display: flex
    .blogs--header-svg
      @media screen and (min-width: 1024px)
        margin-right: 60px
      @media screen and (max-width: 1024px)
        display: flex
        justify-content: center
      svg
        @media screen and (min-width: 1024px)
          width: 160px
          height: 202px
        @media screen and (max-width: 1024px)
          width: 100px
          height: 125px
    .blogs--header-details
      display: flex
      flex-direction: column
      justify-content: center
      .blogs--header-title
        margin-bottom: 20px
        font-weight: 500
        line-height: 130%
        color: #22223C
        @media screen and (min-width: 1024px)
          font-size: 55px
        @media screen and (max-width: 1024px)
          font-size: 30px
          margin: 30px 0 20px
          display: flex
          justify-content: center
      .blogs--header-subtitle
        font-weight: 400
        line-height: 150%
        color: #22223C
        max-width: 680px
        @media screen and (min-width: 1024px)
          font-size: 20px
        @media screen and (max-width: 1024px)
          font-size: 16px
          text-align: center
  .blogs--categories
    display: flex
    flex-direction: row
    flex-wrap: wrap
    grid-gap: 10px
    @media screen and (min-width: 1024px)
      margin-top: 80px
    @media screen and (max-width: 1024px)
      margin-top: 30px
      align-items: center
  .blogs--filter-btn-selected
    background: #007BFF !important
    border: 1px solid #007BFF !important
    svg
      path
        fill: white !important
  .blogs--filter-btn
    cursor: pointer
    border: 1px solid #1F47CD
    border-radius: 10px
    height: 42px
    width: 42px
    display: flex
    justify-content: center
    align-items: center
    transition: 0.3s all ease-in-out
    svg
      path
        transition: 0.3s all ease-in-out
        fill: #1F47CD
  .blogs--category-selected
    background: #1F47CD !important
    color: white !important
    border: 1px solid transparent !important
  .blogs--category
    cursor: pointer
    display: flex
    justify-content: center
    align-items: center
    padding: 5px 15px
    border-radius: 10px
    font-weight: 500
    font-size: 16px
    line-height: 30px
    background: white
    color: #1F47CD
    border: 1px solid #1F47CD
    transition: 0.3s all ease-in-out
  .blogs--mobile-categories
    display: flex
    flex-direction: row
    flex-wrap: wrap
    grid-gap: 10px
    max-height: 0
    overflow: hidden
    margin-top: 10px
  .blogs--body--items
    display: grid
    width: 100%
    @media screen and (min-width: 1024px)
      grid-template-columns: repeat(3, minmax(0, 1fr))
      grid-gap: 40px
      margin-top: 40px
    @media screen and (max-width: 1024px) and (min-width: 600px)
      grid-template-columns: 1fr 1fr
      grid-gap: 35px
      margin-top: 32px
    @media screen and (max-width: 600px)
      margin-top: 20px
      grid-template-columns: 1fr
      grid-gap: 20px
  .infiniteLoading
    display: flex
    justify-content: center
    align-items: center
    margin: 30px 0 90px
</style>
